/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, useCallback, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import { ProformaCard } from '../../components/finance/proforma-card.js';
import { Button } from '../../components/ui/button.js';
import { Modal } from '../../components/ui/modal.js';
import { QueryState } from '../../components/ui/query-state.js';
import { type Proforma } from '../../lib/api/proformas.js';
import {
  useApproveProforma,
  useDeclineProforma,
  useMyProformas,
} from '../../lib/queries/proformas.js';

const INPUT =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';

function ProformaActions({ proforma }: { readonly proforma: Proforma }): JSX.Element {
  const { t } = useTranslation();
  const approve = useApproveProforma();
  const decline = useDeclineProforma();
  const [declining, setDeclining] = useState(false);
  const [reason, setReason] = useState('');

  const onApprove = useCallback(() => {
    approve.mutate(proforma.id);
  }, [approve, proforma.id]);
  const openDecline = useCallback(() => {
    setDeclining(true);
  }, []);
  const close = useCallback(() => {
    setDeclining(false);
  }, []);
  const onModal = useCallback((open: boolean) => {
    if (!open) {
      setDeclining(false);
    }
  }, []);
  const onReason = useCallback((e: ChangeEvent<HTMLTextAreaElement>) => {
    setReason(e.target.value);
  }, []);
  const onDecline = useCallback(() => {
    decline.mutate({ id: proforma.id, reason }, { onSettled: close });
  }, [decline, proforma.id, reason, close]);

  return (
    <>
      <Button type="button" variant="outline" onClick={openDecline}>
        {t('proforma.decline')}
      </Button>
      <Button type="button" onClick={onApprove} disabled={approve.isPending}>
        {t('proforma.approve')}
      </Button>
      <Modal
        open={declining}
        onOpenChange={onModal}
        title={t('proforma.decline')}
        closeLabel={t('common.cancel')}
      >
        <div className="space-y-3">
          <textarea
            rows={3}
            className={INPUT}
            value={reason}
            onChange={onReason}
            placeholder={t('proforma.declineReason')}
          />
          <div className="flex justify-end">
            <Button
              type="button"
              onClick={onDecline}
              disabled={reason.trim() === '' || decline.isPending}
            >
              {t('proforma.decline')}
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
}

/** Parent → Billing → Proformas (CR-008): review + approve/decline sent proformas. */
export function ParentProformasPanel(): JSX.Element | null {
  const { t } = useTranslation();
  const query = useMyProformas();
  const items = query.data ?? [];
  if (!query.isLoading && !query.isError && items.length === 0) {
    return null;
  }
  return (
    <div className="space-y-3">
      <h2 className="text-lg font-semibold text-slate-800 dark:text-white">
        {t('proforma.title')}
      </h2>
      <QueryState
        isLoading={query.isLoading}
        isError={query.isError}
        isEmpty={items.length === 0}
        loadingLabel={t('common.loading')}
        errorLabel={t('common.loadError')}
        emptyLabel={t('proforma.empty')}
      >
        <div className="space-y-3">
          {items.map((proforma) => (
            <ProformaCard key={proforma.id} proforma={proforma}>
              {proforma.status === 'sent' && <ProformaActions proforma={proforma} />}
            </ProformaCard>
          ))}
        </div>
      </QueryState>
    </div>
  );
}
